// client/src/pages/HREmployees.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../api/client'; 
import {
    Box, Typography, Alert, TextField, Button, Chip,
    CircularProgress, Divider, Stack, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,Fade,
    Drawer, Tooltip, Link, Breadcrumbs, Checkbox
} from '@mui/material';
import {
    FileSpreadsheet, ExternalLink, RefreshCw, ShieldAlert, Shield, Users, Layers, 
    Search, X, Activity, Fingerprint, SearchCode, Plus, Trash2, Edit3, Copy, 
    Check, ChevronRight, Info
} from 'lucide-react';

const HREmployees = () => {
    const [employees, setEmployees] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [roleFilter, setRoleFilter] = useState('ALL');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [uiMessage, setUiMessage] = useState({ text: '', isError: false });
    const [isValidatingEmp, setIsValidatingEmp] = useState(false);
    const [copiedId, setCopiedId] = useState('');

    const getInitialFormState = () => ({
        employee_id: '',
        name: '',
        email: '',
        role: '',
        sheets: [{ sheetId: '', label: 'Primary Tracker' }]
    });

    const [formData, setFormData] = useState(getInitialFormState());

    const fetchEmployees = useCallback(async () => {
        try {
            setLoading(true);
            const response = await API.get('/employees');
            setEmployees(response.data.employees || response.data || []);
        } catch (err) {
            console.error('Directory sync error:', err);
            setUiMessage({
                text: 'System Directory Failure: Unable to synchronize local cache with remote IAM core registries.',
                isError: true
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    useEffect(() => {
        const verifyAndFetchEmployeeProfile = async () => {
            if (!formData.employee_id || formData.employee_id.trim().length < 3) return;

            setIsValidatingEmp(true);
            try {
                const response = await API.get(`/employees/${formData.employee_id.trim()}?t=${new Date().getTime()}`);
                const employeeData = response.data?.employee || response.data;

                if (employeeData && Object.keys(employeeData).length > 0) {
                    let structuredSheets = [{ sheetId: '', label: 'Primary Tracker' }];
                    const sheetsSource = employeeData.employee_sheets || employeeData.employeeSheets || employeeData.sheets || employeeData.Sheets;
                    if (sheetsSource && sheetsSource.length > 0) {
                        structuredSheets = sheetsSource.map(s => ({
                            sheetId: s.sheet_id || s.sheetId || '',
                            label: s.label || 'Operational Tracker'
                        }));
                    }

                    setFormData(prev => ({
                        ...prev,
                        name: employeeData.name || 'N/A',
                        email: employeeData.email || 'N/A',
                        role: employeeData.role || 'bde',
                        sheets: structuredSheets
                    }));
                } else {
                    setFormData(prev => ({ ...prev, name: '', email: '', role: '', sheets: [{ sheetId: '', label: 'Primary Tracker' }] }));
                }
            } catch (err) {
                console.error("Employee verification failed:", err);
                setFormData(prev => ({ ...prev, name: '', email: '', role: '', sheets: [{ sheetId: '', label: 'Primary Tracker' }] }));
            } finally {
                setIsValidatingEmp(false);
            }
        };

        const debounceTimer = setTimeout(verifyAndFetchEmployeeProfile, 600);
        return () => clearTimeout(debounceTimer);
    }, [formData.employee_id]);

    const directoryRibbon = useMemo(() => {
        const total = employees.length;
        const admins = employees.filter(emp => String(emp.role).toLowerCase() === 'admin').length;
        const hr = employees.filter(emp => String(emp.role).toLowerCase() === 'hr').length;
        const bde = employees.filter(emp => String(emp.role).toLowerCase() === 'bde').length;

        return [
            { label: 'TOTAL ACTIVE RECORDS', value: total, badgeColor: '#514f4d' },
            { label: 'SYSTEM ADMINISTRATORS', value: admins, badgeColor: '#0176d3' },
            { label: 'HR OPERATIONS', value: hr, badgeColor: '#8430ce' },
            { label: 'BUSINESS DEVELOPMENT (BDE)', value: bde, badgeColor: '#b05e03' }
        ];
    }, [employees]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedEmployees(filteredEmployees.map(emp => emp.id));
        } else {
            setSelectedEmployees([]);
        }
    };

    const handleSelectRow = (id) => {
        setSelectedEmployees(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSheetRowChange = (index, field, value) => {
        const updatedSheets = [...formData.sheets];
        updatedSheets[index][field] = value;
        setFormData(prev => ({ ...prev, sheets: updatedSheets }));
    };

    const addSheetRow = () => {
        setFormData(prev => ({
            ...prev,
            sheets: [...prev.sheets, { sheetId: '', label: `Tracker Asset ${prev.sheets.length + 1}` }]
        }));
    };

    const removeSheetRow = (index) => {
        if (formData.sheets.length === 1) {
            handleSheetRowChange(0, 'sheetId', '');
            return;
        }
        const updatedSheets = formData.sheets.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, sheets: updatedSheets }));
    };

    const handleEditRowClick = (employeeId) => {
        setFormData(prev => ({ ...prev, employee_id: employeeId }));
        setIsDrawerOpen(true);
    };

    const handleCopyId = (idString) => {
        navigator.clipboard.writeText(idString);
        setCopiedId(idString);
        setTimeout(() => setCopiedId(''), 2000);
    };

    const handleClearForm = () => {
        setFormData(getInitialFormState());
    };

    const handleProvisionGoogleSheet = async (e) => {
        e.preventDefault();
        if (!formData.name || formData.name === 'N/A') {
            setUiMessage({ text: 'Cannot assign configurations to an unverified identity context.', isError: true });
            return;
        }

        const activeSheetsPayload = formData.sheets.filter(s => s.sheetId.trim() !== '');
        setUiMessage({ text: '', isError: false });
        setSubmitting(true);

        try {
            const response = await API.post('/auth/register-employee-sheet', {
                employee_id: formData.employee_id,
                sheets: activeSheetsPayload
            });

            setUiMessage({
                text: response.data.message || 'Google Sheet layout assignments mapped completely.',
                isError: false
            });
            setFormData(getInitialFormState());
            setIsDrawerOpen(false);
            fetchEmployees();
        } catch (err) {
            setUiMessage({
                text: err.response?.data?.message || 'Transaction error: Deployment parameters rejected.',
                isError: true
            });
        } finally {
            setSubmitting(false);
        }
    };

    const filteredEmployees = useMemo(() => {
        return employees.filter(emp => {
            const matchesSearch = 
                emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.id?.toString().toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchesFilter = roleFilter === 'ALL' || String(emp.role).toUpperCase() === roleFilter;

            return matchesSearch && matchesFilter;
        });
    }, [employees, searchQuery, roleFilter]);

    const getRoleStyling = (role) => {
        const normalizedRole = String(role).toLowerCase();
        const matrix = {
            admin: { bg: '#e0f2fe', text: '#0176d3', border: '#b8e3fe', icon: <Shield size={11} /> },
            hr: { bg: '#f3e8ff', text: '#8430ce', border: '#e9d5ff', icon: <Users size={11} /> },
            bde: { bg: '#fef3c7', text: '#b05e03', border: '#fde68a', icon: <Layers size={11} /> }
        };
        return matrix[normalizedRole] || { bg: '#f3f2f1', text: '#514f4d', border: '#dddbda', icon: null };
    };

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f3f2f1', overflow: 'hidden', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            
            {/* LIGHTNING UTILITY HEADER CONSOLE */}
            <Box sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #dddbda', px: 3, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <Box>
                    <Breadcrumbs separator={<ChevronRight size={10} />} sx={{ fontSize: '11px', mb: 0.2, color: '#514f4d', fontWeight: 500 }}>
                        <Link underline="none" color="inherit" href="#" sx={{ '&:hover': { color: '#0176d3' } }}>Salesforce Identity Console</Link>
                        <Typography color="text.primary" sx={{ fontSize: '11px', fontWeight: 600, color: '#181816' }}>Directory Mapping Engine</Typography>
                    </Breadcrumbs>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#081c3b', fontSize: '20px', display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        Workspace Members
                        <Chip size="small" icon={<Fingerprint size={12} style={{ color: '#0176d3' }} />} label="System-of-Record (Active)" sx={{ height: '22px', fontSize: '10px', bgcolor: '#e0f2fe', color: '#0176d3', fontWeight: 700, borderRadius: '4px', border: '1px solid #b8e3fe' }} />
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Button 
                        variant="outlined" size="small" onClick={fetchEmployees} disabled={loading}
                        startIcon={<RefreshCw size={13} className={loading ? "animate-spin" : ""} />}
                        sx={{ color: '#0176d3', borderColor: '#dddbda', textTransform: 'none', fontWeight: 700, height: '32px', fontSize: '12px', bgcolor: '#ffffff', borderRadius: '4px', '&:hover': { bgcolor: '#f3f2f1', borderColor: '#0176d3' } }}
                    >
                        Refresh Node
                    </Button>
                    <Button 
                        variant="contained" size="small" startIcon={<Plus size={14} />} onClick={() => setIsDrawerOpen(true)}
                        sx={{ bgcolor: '#0176d3', textTransform: 'none', fontWeight: 700, height: '32px', fontSize: '12px', borderRadius: '4px', boxShadow: 'none', '&:hover': { bgcolor: '#015aaf', boxShadow: 'none' } }}
                    >
                        New Mapping Schema
                    </Button>
                </Box>
            </Box>

            {/* LIGHTNING CORE PERFORMANCE BANNER */}
            <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, overflow: 'hidden' }}>
                
                {/* SYSTEM METRIC KPI CARDS */}
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', px: 3, py: 1.8, bgcolor: '#ffffff', borderRadius: '4px', border: '1px solid #dddbda', flexShrink: 0, overflowX: 'auto', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, borderRight: '1px solid #dddbda', pr: 4, flexShrink: 0 }}>
                        <Activity size={16} color="#0176d3" />
                        <Typography variant="caption" sx={{ mountaineer: 'normal', fontWeight: 800, color: '#514f4d', letterSpacing: '0.06em', fontSize: '11px' }}>
                            METRICS OVERVIEW
                        </Typography>
                    </Box>
                    {directoryRibbon.map((metric, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: metric.badgeColor }} />
                            <Box>
                                <Typography variant="caption" sx={{ color: '#878685', fontWeight: 700, display: 'block', fontSize: '10px', letterSpacing: '0.02em' }}>
                                    {metric.label}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#181816', fontWeight: 800, fontSize: '16px', mt: -0.2 }}>
                                    {loading ? '—' : metric.value}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* LOGISTICAL REAL-TIME ALERTS */}
                {uiMessage.text && (
                    <Fade in={Boolean(uiMessage.text)}>
                        <Box sx={{ flexShrink: 0 }}>
                            <Alert
                                severity={uiMessage.isError ? "error" : "success"}
                                icon={uiMessage.isError ? <ShieldAlert size={16} /> : null}
                                action={
                                    <IconButton aria-label="close" color="inherit" size="small" onClick={() => setUiMessage({ text: '', isError: false })}>
                                        <X size={14} />
                                    </IconButton>
                                }
                                sx={{
                                    borderRadius: '4px', fontWeight: 600, fontSize: '13px', py: 0.75, px: 2,
                                    border: uiMessage.isError ? '1px solid #ea001e' : '1px solid #4bca81',
                                    bgcolor: uiMessage.isError ? '#ffeeec' : '#eafbf2', color: uiMessage.isError ? '#ea001e' : '#2e844a'
                                }}
                            >
                                {uiMessage.text}
                            </Alert>
                        </Box>
                    </Fade>
                )}

                {/* SALESFORCE DATAGRID CONTROL CONSOLE CONTROL PANEL */}
                <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#ffffff', border: '1px solid #dddbda', borderRadius: '4px', flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid #f3f2f1', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: '280px', border: '1px solid #g7g6g5', px: 2, py: 0.5, borderRadius: '4px', '&:focus-within': { borderColor: '#0176d3', boxShadow: '0 0 3px #0176d3' } }}>
                            <Search size={15} style={{ color: '#747473' }} />
                            <TextField
                                variant="standard" 
                                placeholder="Search workspace context via names, explicit signatures, structural attributes..."
                                fullWidth value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{ disableUnderline: true, sx: { fontSize: '13px', color: '#181816' } }}
                            />
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip 
                                label="Show All" size="small" onClick={() => setRoleFilter('ALL')}
                                sx={{ borderRadius: '4px', fontSize: '11px', fontWeight: 700, bgcolor: roleFilter === 'ALL' ? '#0176d3' : '#f3f2f1', color: roleFilter === 'ALL' ? '#ffffff' : '#514f4d', '&:hover': { bgcolor: roleFilter === 'ALL' ? '#015aaf' : '#e0e0e0' } }} 
                            />
                            <Chip 
                                icon={<Shield size={10} />} label="ADMINS" size="small" onClick={() => setRoleFilter('ADMIN')}
                                sx={{ borderRadius: '4px', fontSize: '11px', fontWeight: 700, bgcolor: roleFilter === 'ADMIN' ? '#0176d3' : '#f3f2f1', color: roleFilter === 'ADMIN' ? '#ffffff' : '#514f4d' }} 
                            />
                            <Chip 
                                icon={<Users size={10} />} label="HR" size="small" onClick={() => setRoleFilter('HR')}
                                sx={{ borderRadius: '4px', fontSize: '11px', fontWeight: 700, bgcolor: roleFilter === 'HR' ? '#0176d3' : '#f3f2f1', color: roleFilter === 'HR' ? '#ffffff' : '#514f4d' }} 
                            />
                            <Chip 
                                icon={<Layers size={10} />} label="BDE" size="small" onClick={() => setRoleFilter('BDE')}
                                sx={{ borderRadius: '4px', fontSize: '11px', fontWeight: 700, bgcolor: roleFilter === 'BDE' ? '#0176d3' : '#f3f2f1', color: roleFilter === 'BDE' ? '#ffffff' : '#514f4d' }} 
                            />
                        </Stack>
                    </Box>

                    {/* BULK SELECTION ACTION CONTROL STRIP */}
                    {selectedEmployees.length > 0 && (
                        <Box sx={{ bg: '#16325c', bgcolor: '#002b66', color: '#ffffff', px: 3, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px' }}>
                                <Info size={14} /> Selected records matrix scope: {selectedEmployees.length} entities locked.
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Button size="small" variant="text" sx={{ color: '#ffffff', textTransform: 'none', fontWeight: 700, fontSize: '11px' }}>
                                    Batch Export Data Metadata
                                </Button>
                                <Button size="small" variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '11px', px: 2, boxShadow: 'none' }}>
                                    Revoke Operational Access Tokens
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </Box>

                {/* THE CORE ENTERPRISE DATAGRID */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#ffffff', border: '1px solid #dddbda', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                    <TableContainer sx={{ height: '100%', overflowY: 'auto' }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <CircularProgress size={30} thickness={4.5} sx={{ color: '#0176d3' }} />
                            </Box>
                        ) : (
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" sx={{ bgcolor: '#fafaf9', borderBottom: '1px solid #dddbda', pl: 2 }}>
                                            <Checkbox 
                                                size="small"
                                                indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < filteredEmployees.length}
                                                checked={filteredEmployees.length > 0 && selectedEmployees.length === filteredEmployees.length}
                                                onChange={handleSelectAll}
                                                sx={{ color: '#dddbda', '&.Mui-checked': { color: '#0176d3' } }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ bgcolor: '#fafaf9', fontWeight: 800, color: '#514f4d', fontSize: '11px', borderBottom: '1px solid #dddbda', py: 1.5, letterSpacing: '0.03em' }}>RESOURCE NAME & ID POINTER</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafaf9', fontWeight: 800, color: '#514f4d', fontSize: '11px', borderBottom: '1px solid #dddbda', py: 1.5, letterSpacing: '0.03em' }}>SECURITY LAYER ACCESS PROFILE</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafaf9', fontWeight: 800, color: '#514f4d', fontSize: '11px', borderBottom: '1px solid #dddbda', py: 1.5, letterSpacing: '0.03em' }}>INTEGRATED RECORD WORKBOOKS & ALPHANUMERIC TARGET LEDGERS</TableCell>
                                        <TableCell align="right" sx={{ bgcolor: '#fafaf9', fontWeight: 800, color: '#514f4d', fontSize: '11px', borderBottom: '1px solid #dddbda', py: 1.5, pr: 3, letterSpacing: '0.03em' }}>ACTION SCHEMAS</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredEmployees.map((emp) => {
                                        const roleStyle = getRoleStyling(emp.role);
                                        const explicitSheets = emp.employee_sheets || emp.employeeSheets || emp.sheets || emp.Sheets || [];
                                        const isRowChecked = selectedEmployees.includes(emp.id);

                                        return (
                                            <TableRow key={emp.id} hover selected={isRowChecked} sx={{ bgcolor: isRowChecked ? '#f4f6f9' : 'inherit', '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#fafaf9' } }}>
                                                <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #f3f2f1', pl: 2 }}>
                                                    <Checkbox 
                                                        size="small" checked={isRowChecked} onChange={() => handleSelectRow(emp.id)}
                                                        sx={{ color: '#dddbda', '&.Mui-checked': { color: '#0176d3' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 1.6, borderBottom: '1px solid #f3f2f1' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0176d3', fontSize: '13px', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                                            {emp.name}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#514f4d', mt: 0.3, fontSize: '11px' }}>
                                                            {emp.email} • <span style={{ color: '#747473', fontWeight: 700 }}>Index Token: {emp.id}</span>
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.6, borderBottom: '1px solid #f3f2f1' }}>
                                                    <Chip
                                                        icon={roleStyle.icon} 
                                                        label={String(emp.role || 'Scope Exempt').toUpperCase()} 
                                                        size="small"
                                                        sx={{ fontWeight: 800, fontSize: '10px', borderRadius: '3px', bgcolor: roleStyle.bg, color: roleStyle.text, border: `1px solid ${roleStyle.border}`, height: '20px', '& .MuiChip-icon': { color: 'inherit' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 1.6, borderBottom: '1px solid #f3f2f1' }}>
                                                    <Stack spacing={1} direction="row" useFlexGap flexWrap="wrap">
                                                        {explicitSheets.length > 0 ? (
                                                            explicitSheets.map((sheet, index) => {
                                                                const rawSheetId = sheet.sheet_id || sheet.sheetId || '';
                                                                const sheetLink = `https://docs.google.com/spreadsheets/d/${rawSheetId}`;

                                                                return (
                                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.4, px: 1.2, borderRadius: '3px', border: '1px solid #dddbda', bgcolor: '#fafaf9', transition: 'all 0.15s ease', '&:hover': { borderColor: '#a8a8a7', bgcolor: '#f3f2f1' } }}>
                                                                        <Button
                                                                            variant="text" size="small" startIcon={<FileSpreadsheet size={11} />} endIcon={<ExternalLink size={9} />}
                                                                            href={sheetLink} target="_blank" rel="noopener noreferrer"
                                                                            sx={{ textTransform: 'none', fontWeight: 700, color: '#2e844a', p: 0, minWidth: 0, fontSize: '11px', '&:hover': { color: '#194c2a', background: 'transparent' } }}
                                                                        >
                                                                            {sheet.label || 'Linked Target'}
                                                                        </Button>
                                                                        <Tooltip title={copiedId === rawSheetId ? "Copied Ledger Key!" : "Copy Ledger Key"}>
                                                                            <IconButton size="small" onClick={() => handleCopyId(rawSheetId)} sx={{ p: 0.1, color: '#747473' }}>
                                                                                {copiedId === rawSheetId ? <Check size={11} color="#4bca81" /> : <Copy size={11} />}
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                );
                                                            })
                                ) : (
                                                            <Typography variant="caption" sx={{ color: '#747473', fontWeight: 600, fontSize: '11px', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                {String(emp.role).toLowerCase() === 'bde' ? '⚠️ Configuration Mapping Pipeline Missing' : 'Workbook Mapping Exempt Profile'}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 1.6, pr: 3, borderBottom: '1px solid #f3f2f1' }}>
                                                    <Tooltip title="Modify Structural Layout Elements">
                                                        <IconButton size="small" onClick={() => handleEditRowClick(emp.id)} sx={{ color: '#0176d3', border: '1px solid #dddbda', borderRadius: '4px', p: 0.4, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f3f2f1' } }}>
                                                            <Edit3 size={13} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {filteredEmployees.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 12, color: '#747473', fontSize: '13px', fontWeight: 500 }}>
                                                Zero core database entity configurations matched the input processing matrix parameters.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </TableContainer>
                </Box>
            </Box>

            {/* LIGHTNING DRAWER ENGINE SHEETS SIDE CONSOLE */}
            <Drawer anchor="right" open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100vw', sm: '480px' }, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #dddbda' } }}>
                <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #dddbda', bgcolor: '#fafaf9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FileSpreadsheet size={18} color="#0176d3" />
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#181816', fontSize: '15px' }}>Asset Distribution Mapping</Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: '#514f4d', fontSize: '11px' }}>Map distributed live cloud storage nodes to target directories.</Typography>
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={() => setIsDrawerOpen(false)} sx={{ border: '1px solid #dddbda', p: 0.4 }}><X size={14} /></IconButton>
                </Box>

                <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto', bgcolor: '#ffffff' }}>
                    <form onSubmit={handleProvisionGoogleSheet} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <TextField
                            fullWidth required size="small" label="Employee Identification Target Index Pointer" name="employee_id"
                            placeholder="Provide systemic corporate key pointer (e.g. TG1002)..." value={formData.employee_id} onChange={handleInputChange}
                            InputProps={{
                                sx: { borderRadius: '4px', fontSize: '13px' },
                                endAdornment: isValidatingEmp ? <CircularProgress size={14} color="inherit" /> : <SearchCode size={14} color="#747473" />
                            }}
                            InputLabelProps={{ sx: { fontSize: '12px', fontWeight: 800, color: '#514f4d', mb: 0.5 } }}
                        />

                        <Divider><Chip label="Active Registry Profile Identity Mapping" size="small" sx={{ fontSize: '10px', fontWeight: 800, bgcolor: '#f3f2f1', color: '#514f4d', borderRadius: '3px' }} /></Divider>

                        <TextField
                            fullWidth disabled size="small" label="Verified Identity Full Name" name="name"
                            value={formData.name} placeholder="Awaiting dynamic catalog profile query validation..."
                            InputProps={{ sx: { borderRadius: '4px', fontSize: '12px', bgcolor: '#fafaf9', color: '#181816' } }}
                            InputLabelProps={{ sx: { fontSize: '11px', fontWeight: 700 } }}
                        />
                        <TextField
                            fullWidth disabled size="small" label="Active Corporate Communication Node Vector" name="email"
                            value={formData.email} placeholder="Awaiting dynamic catalog profile query validation..."
                            InputProps={{ sx: { borderRadius: '4px', fontSize: '12px', bgcolor: '#fafaf9', color: '#181816' } }}
                            InputLabelProps={{ sx: { fontSize: '11px', fontWeight: 700 } }}
                        />
                        <TextField
                            fullWidth disabled size="small" label="Assigned Structural Governance Scope Privilege Layer" name="role"
                            value={formData.role ? String(formData.role).toUpperCase() : ""} placeholder="Awaiting dynamic catalog profile query validation..."
                            InputProps={{ sx: { borderRadius: '4px', fontSize: '12px', bgcolor: '#fafaf9', color: '#181816' } }}
                            InputLabelProps={{ sx: { fontSize: '11px', fontWeight: 700 } }}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#514f4d', letterSpacing: '0.04em', fontSize: '11px' }}>
                                RELATIONAL LEDGERS STRUCTURAL MATRIX
                            </Typography>
                            <Button size="small" startIcon={<Plus size={11} />} onClick={addSheetRow} sx={{ textTransform: 'none', fontWeight: 700, fontSize: '11px', color: '#0176d3' }}>
                                Append Matrix Segment Node
                            </Button>
                        </Box>

                        <Stack spacing={2}>
                            {formData.sheets.map((sheet, index) => (
                                <Box key={index} sx={{ p: 2, border: '1px solid #dddbda', borderRadius: '4px', bgcolor: '#fafaf9', display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                    <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
                                        <TextField
                                            fullWidth required size="small" label="Cloud Asset Label Namespace"
                                            placeholder="e.g., Primary, Operations Hub Workspace Layout Cluster..." value={sheet.label}
                                            onChange={(e) => handleSheetRowChange(index, 'label', e.target.value)}
                                            InputProps={{ sx: { borderRadius: '4px', fontSize: '12px', bgcolor: '#ffffff' } }}
                                            InputLabelProps={{ sx: { fontSize: '11px' } }}
                                        />
                                        <TextField
                                            fullWidth required size="small" label="Google Spreadsheet Base Alphanumeric Asset Signature String"
                                            placeholder="Long-form unique cryptographic spreadsheet link hash..." value={sheet.sheetId}
                                            onChange={(e) => handleSheetRowChange(index, 'sheetId', e.target.value)}
                                            InputProps={{ sx: { borderRadius: '4px', fontSize: '12px', bgcolor: '#ffffff' } }}
                                            InputLabelProps={{ sx: { fontSize: '11px' } }}
                                        />
                                    </Stack>
                                    <IconButton size="small" color="error" onClick={() => removeSheetRow(index)} sx={{ mt: 0.5, border: '1px solid #f3f2f1', bgcolor: '#ffffff', '&:hover': { bgcolor: '#ffeeec' } }}>
                                        <Trash2 size={13} />
                                    </IconButton>
                                </Box>
                            ))}
                        </Stack>

                        <Box sx={{ mt: 'auto', pt: 4 }}>
                            <Divider sx={{ mb: 2.5 }} />
                            <Stack direction="row" spacing={2}>
                                <Button fullWidth type="button" variant="text" onClick={handleClearForm} sx={{ textTransform: 'none', fontWeight: 700, fontSize: '13px', color: '#514f4d', '&:hover': { bgcolor: '#f3f2f1' } }}>
                                    Reset Workspace Parameters
                                </Button>
                                <Button
                                    fullWidth type="submit" variant="contained" disabled={submitting || !formData.name || formData.name === 'N/A'}
                                    sx={{ bgcolor: '#0176d3', textTransform: 'none', fontWeight: 700, py: 1.2, borderRadius: '4px', fontSize: '13px', boxShadow: 'none', '&:hover': { bgcolor: '#015aaf', boxShadow: 'none' } }}
                                >
                                    {submitting ? <CircularProgress size={14} sx={{ color: '#ffffff' }} /> : 'Commit Workspace Matrix'}
                                </Button>
                            </Stack>
                        </Box>
                    </form>
                </Box>
            </Drawer>
        </Box>
    );
};

export default HREmployees;